/**
 * Tenant-path helpers.
 *
 * The app supports two URL shapes:
 *   • Legacy single-tenant: /, /settings, /profile, /users, /investigation/:id
 *   • Multi-tenant:         /t/<slug>, /t/<slug>/settings, …
 *
 * When the operator is on the multi-tenant shape, every internal
 * navigation MUST preserve `/t/<slug>` so the tenant context isn't
 * stripped on a query-param cleanup or a nav click.
 *
 * These helpers are pure; the URL is purely cosmetic — the JWT /
 * user.tenant_id remain the authoritative tenancy boundary.
 */

const TENANT_PREFIX_RE = /^\/t\/([^/?#]+)/;

/**
 * Extract the tenant base path from a router `location.pathname`.
 *   tenantBasePath('/t/grassboots')                → '/t/grassboots'
 *   tenantBasePath('/t/grassboots/settings')       → '/t/grassboots'
 *   tenantBasePath('/t/grassboots/investigation/x')→ '/t/grassboots'
 *   tenantBasePath('/settings')                    → ''
 *   tenantBasePath('/')                            → ''
 */
export function tenantBasePath(pathname) {
  if (typeof pathname !== 'string') return '';
  const m = TENANT_PREFIX_RE.exec(pathname);
  return m ? `/t/${m[1]}` : '';
}

/**
 * Build a tenant-aware URL.
 *   • Under /t/<slug>: prefixes the supplied app-relative path.
 *   • Otherwise: returns the path verbatim.
 *
 * `path` MUST start with '/' (or be empty for the tenant root).
 *   tenantPath('/t/grassboots', '')           → '/t/grassboots'
 *   tenantPath('/t/grassboots', '/')          → '/t/grassboots'
 *   tenantPath('/t/grassboots', '/settings')  → '/t/grassboots/settings'
 *   tenantPath('', '/settings')               → '/settings'
 *   tenantPath('', '/')                       → '/'
 */
export function tenantPath(basePath, path) {
  const base = basePath || '';
  const rel = path || '/';
  if (!base) return rel;
  if (rel === '/' || rel === '') return base;
  return `${base}${rel.startsWith('/') ? rel : `/${rel}`}`;
}

/**
 * Convenience for use inside React components:
 *   const tp = useTenantPath();
 *   navigate(tp('/settings'));
 *
 * Wraps the helpers above against the current `location.pathname`.
 * Caller is responsible for `useLocation()` themselves to avoid a
 * mandatory react-router dependency on the lib.
 */
export function makeTenantPathBuilder(pathname) {
  const base = tenantBasePath(pathname);
  return (path) => tenantPath(base, path);
}
